import React, { useState } from 'react';
import { BrandVisa, BrandMC, BrandMaestro, BrandKlarna, BrandApplePay, BrandGooglePay } from './PayBrands';

/*
 * Trust strip under the summary total: the marks a shopper already recognises from
 * every other checkout — the card schemes and wallets we actually accept, led by a
 * plain SSL padlock. Recognition is the whole point of a trust badge, so these are
 * the real marks rather than emblems invented for this site.
 *
 * What is deliberately NOT here: Norton Secured / McAfee SECURE / TRUSTe / BBB and
 * friends. Those are certification marks ISSUED to a site after (paid) verification.
 * Drawing a lookalike would be a credential we don't hold, sitting on a live
 * storefront where customers click it. If we want one, we buy it and paste the
 * issuer's own snippet — the Greek options worth having are the Skroutz «Αξιόπιστο
 * Κατάστημα» mark (we already feed Skroutz) and the GRECA trustmark.
 *
 * Marks alone can be read as "which cards can I use?", so each one explains itself
 * on hover (pointer) or tap (touch) — the SSL badge carries the encryption claim,
 * every other tile says what paying with it means. TLS 1.3 / TLS_AES_256_GCM_SHA384
 * is what the edge actually negotiates, so "SSL 256-bit" is the real cipher.
 *
 * The bubble is anchored to the STRIP, not to a tile: with the row wrapping, a
 * tile-anchored bubble on an edge badge would hang outside the 320px card and could
 * push a horizontal scrollbar onto the page.
 */

// Secure-connection mark: padlock + "SSL" on green. Generic by design — this is the
// one badge every store draws for itself, and it certifies nothing but the transport.
const BadgeSSL = () => (
  <svg width="34" height="22" viewBox="0 0 34 22" role="img" aria-label="SSL">
    <rect width="34" height="22" rx="3" fill="#0A8F3C" />
    <path d="M7.9 9.7V8.2a2.85 2.85 0 0 1 5.7 0v1.5" fill="none" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
    <rect x="6.7" y="9.5" width="8.1" height="6.6" rx="1.7" fill="#fff" />
    <circle cx="10.75" cy="12.2" r="1" fill="#0A8F3C" />
    <path d="M10.75 13v1.3" stroke="#0A8F3C" strokeWidth="1.1" strokeLinecap="round" />
    <text x="24.2" y="15" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="700" fontSize="8" fill="#fff">SSL</text>
  </svg>
);

const ITEMS = [
  { badge: <BadgeSSL />, note: 'Ασφαλής σύνδεση SSL 256-bit' },
  { badge: <BrandVisa />, note: 'Visa — με 3-D Secure από την τράπεζά σου' },
  { badge: <BrandMC />, note: 'Mastercard — με 3-D Secure από την τράπεζά σου' },
  { badge: <BrandMaestro />, note: 'Maestro — με 3-D Secure από την τράπεζά σου' },
  { badge: <BrandKlarna />, note: 'Πληρωμή με Klarna' },
  { badge: <BrandApplePay />, note: 'Πληρωμή με Apple Pay' },
  { badge: <BrandGooglePay />, note: 'Πληρωμή με Google Pay' },
];

export default function TrustBadges() {
  const [active, setActive] = useState(-1);

  return (
    <div className="cn-trust" role="group" aria-label="Ασφάλεια και τρόποι πληρωμής">
      <span className={`cn-trust__pop ${active >= 0 ? 'is-open' : ''}`} aria-hidden="true">
        {active >= 0 ? ITEMS[active].note : ''}
      </span>
      <div className="cn-trust__row">
        {ITEMS.map((it, i) => (
          <button
            type="button"
            className={`cn-trust__chip ${active === i ? 'is-active' : ''}`}
            key={i}
            aria-label={it.note}
            // Hover is mouse-only: a touch pointerenter fires right before the tap
            // and would toggle the bubble open-then-shut in one gesture.
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') setActive(i); }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') setActive((a) => (a === i ? -1 : a)); }}
            onFocus={() => setActive(i)}
            // Tapping elsewhere blurs the chip — that's the close gesture, so no
            // document-level listener is needed.
            onBlur={() => setActive((a) => (a === i ? -1 : a))}
            onClick={() => setActive(i)}
          >
            {it.badge}
          </button>
        ))}
      </div>
    </div>
  );
}
